import {auctionTableStyle} from '@/components/Organization/Auction/AuctionTable/AuctionTable.styles';
import {
  BID,
  BODY1,
  defaultPageSize,
  HASH,
  IMAGE_TABLE_ALT,
  ITEM,
  SEARCH_FOR_WINNER,
  SUBTITLE1,
  TABLE,
  TABLE_NAME,
  WIN_BID,
  WINNER_NAME,
  ADD_WINNER,
  CANCEL,
  SAVE_CHANGES,
  defaultPage,
} from '@/utils/constants';
import {
  Box,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {WinnerTableStyle} from './WinnerTableStyle.styles';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAuctionCategoriesSelector, getAuctionWonList, getAuctionWonListSelector} from '@/store/organization-auction';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import AuctionInfo from '../AuctionInfo';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {ITeamRole, MENU_OPTION, PAYMENT_STATUS, ROUTER_URL, TABLES_ENTITIES} from '@/utils/enums';
import Button from '@/shared/components/Button';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {useNavigate} from 'react-router-dom';
import {OrganizationAuctionService} from '@/utils/services';
import {IOrganizationAuctionResponse} from '@/utils/types';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {useEffect, useRef, useState} from 'react';
import Loader from '@/shared/components/Loader';
import {auctionStyle} from '@/pages/Organization/Auction/Auction.styles';
import {attendeesTableStyle} from '@/components/Organization/Attendees/AttendeesTable/AttendeesTable.styles';
import Modal from '@/shared/components/Modal';
import AddWinnerPage from '@/pages/Organization/AddWinnerPage';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
import Pagination from '@/shared/components/Pagination';
import {getCurrentEventSelector} from '@/store/organization-event';
import {UserBidService} from '@/utils/services/user-bid.service';
import {currencyFormatter} from '@/utils/helpers/currency';
import {getDashboardStatisticsSelector} from '@/store/organization-dashboard';
import {getTeamSelector} from '@/store/organization';

export const tableActionEntryMenuContent = [
  MENU_OPTION.GO_TO_PROFILE,
  MENU_OPTION.MARK_AS_PAID,
  MENU_OPTION.CANCEL_WIN,
];

const WinnerTable = ({isDataLoaded}: {isDataLoaded: boolean}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formikRef = useRef<any>(null);

  const isMobile = useMediaQuery('(max-width:767.98px)');
  const isLaptop = useMediaQuery('(min-width:1200px)');

  const handleAddWinner = () => {
    if (formikRef.current) {
      formikRef.current.handleSubmit();
    }
  };

  const navigateToProfile = (id: number) => {
    if (isMobile) navigate(`${ROUTER_URL.ORGANIZATION_WINNER_PROFILE}/${id}`);
  };
  const navigateToAddWinner = () => {
    navigate('/organization/auction-entry/add-winner');
  };

  const categories = useAppSelector(getAuctionCategoriesSelector);
  const attendee = useAppSelector(getAttendeeSelector);
  const auctionWonList = useAppSelector(getAuctionWonListSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const statistics = useAppSelector(getDashboardStatisticsSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [isAddWinnerModalOpen, setIsAddWinnerModalOpen] = useState(false);
  const [sortState, setSortState] = useState<{column: string | null; direction: 'asc' | 'desc'}>({
    column: null,
    direction: 'asc',
  });
  const [page, setPage] = useState<number>(defaultPage);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isNoifyModalOpen, setIsNoifyModalOpen] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleWinnerModal = () => {
    if (!currentEvent?.tableInfos.length) {
      handleNotifyModalChange('Add a table tier before you can assign winners.');
      return;
    }
    if (!statistics?.allGuests) {
      handleNotifyModalChange('Add attendees before you can assign winners.');
      return;
    }
    if (!statistics.allItems) {
      handleNotifyModalChange('Add auction items before you can assign winners.');
      return;
    }
    setIsAddWinnerModalOpen(!isAddWinnerModalOpen);
  };
  const handleCloseNotifyModal = () => {
    setNotifyMessage('');
    setIsNoifyModalOpen(false);
  };

  const handleNotifyModalChange = (message?: string) => {
    if (message) setNotifyMessage(message);
    setIsNoifyModalOpen(!isNoifyModalOpen);
  };

  const handleDeleteAuctionWinner = async (auction: IOrganizationAuctionResponse) => {
    if (auction.paymentStatus === PAYMENT_STATUS.PAID) return;

    try {
      await UserBidService.deleteWinner(auction.id);
      await getAuctionListData();

      toast.success('Winner removed from the item');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMakeAuctionPaid = async (auction: IOrganizationAuctionResponse) => {
    if (auction.paymentStatus === PAYMENT_STATUS.PAID) return;

    if (isStandartMember) {
      handleAccessModalChange();
      return;
    }

    try {
      await OrganizationAuctionService.updateAuctionPaymentStatus(auction.id, true);
      toast.success('Auction item marked as paid successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleClickMenuOption = async (action: MENU_OPTION, item: IOrganizationAuctionResponse) => {
    switch (action) {
      case MENU_OPTION.CANCEL_WIN:
        if (item.id) {
          await handleDeleteAuctionWinner(item);
        }
        break;

      case MENU_OPTION.MARK_AS_PAID:
        if (item.id) {
          await handleMakeAuctionPaid(item);
        }
        break;

      case MENU_OPTION.GO_TO_PROFILE:
        if (item) {
          if (isLaptop) {
            handleChangeAttendeeDetailsModal(item.winnerDetails?.bidderId);
            return;
          }
          navigate(`/organization/attendees/${item.winnerDetails?.bidderId}`);
        }
        break;

      default:
        break;
    }
  };

  const handleSortChange = (column: string) => {
    setSortState((prev) => ({
      column,

      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const getAuctionListData = async () => {
    if (!currentEvent?.id) return;

    try {
      const reqBody = {
        eventId: currentEvent?.id as number,
        page: isLaptop ? page : 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
        searchName: searchValue,
      };

      await dispatch(getAuctionWonList({id: +currentEvent?.id, reqBody})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (currentEvent?.id) getAuctionListData();
  }, [currentEvent?.id, searchValue, page]);

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  return (
    <Box sx={WinnerTableStyle.wrapper}>
      <Box>
        <AuctionInfo />
      </Box>

      <Box sx={WinnerTableStyle.wrapperTable}>
        <SearchHeader
          page={1}
          items={auctionWonList!.totalCount < defaultPageSize ? auctionWonList!.totalCount : defaultPageSize}
          total={auctionWonList?.totalCount}
          searchPlaceholder={SEARCH_FOR_WINNER}
          onPressEnter={() => {}}
          onSearchChange={setSearchValue}
          parentClassName={{padding: '0 16px'}}
          unitName={TABLES_ENTITIES.GUESTS}
          actionButton={
            <Button
              onClick={handleWinnerModal}
              title={ADD_WINNER}
              startIcon={<PlusIcon />}
              sx={attendeesTableStyle.searchButton}
            />
          }
        />
        {isDataLoaded ? (
          auctionWonList && auctionWonList?.data.length > 0 ? (
            <Box sx={WinnerTableStyle.tableContent}>
              <TableContainer sx={WinnerTableStyle.container}>
                <Table sx={WinnerTableStyle.table}>
                  <TableHead>
                    <TableRow sx={WinnerTableStyle.tableHeader}>
                      <TableCell>
                        {BID} {HASH}
                      </TableCell>

                      <TableCell>{WINNER_NAME}</TableCell>

                      <TableCell>
                        <TableSortLabel
                          active
                          onClick={() => handleSortChange('tableNumber')}
                          IconComponent={UpDownIcon}
                        >
                          {TABLE} {HASH}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>
                        <TableSortLabel active onClick={() => handleSortChange('tableName')} IconComponent={UpDownIcon}>
                          {TABLE_NAME}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>{ITEM}</TableCell>

                      <TableCell>
                        <TableSortLabel
                          active
                          onClick={() => handleSortChange('winBidAmount')}
                          IconComponent={UpDownIcon}
                        >
                          {WIN_BID}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>{}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {auctionWonList.data.map((row) => (
                      <TableRow key={row.id} sx={WinnerTableStyle.tableBody} onClick={() => navigateToProfile(row?.id)}>
                        <TableCell>{row.winnerDetails?.bidNumber}</TableCell>

                        <TableCell>{`${row.winnerDetails?.firstName} ${row.winnerDetails?.lastName}` || ''}</TableCell>

                        <TableCell>{row.winnerDetails?.tableNumber || ''}</TableCell>

                        <TableCell>{row.winnerDetails?.tableName || ''}</TableCell>

                        <TableCell>
                          <Box sx={WinnerTableStyle.bodyCell}>
                            <Box sx={WinnerTableStyle.bodyCellImage}>
                              {row.itemImages?.[0]?.image ? (
                                <img src={row.itemImages[0].image} alt={IMAGE_TABLE_ALT} />
                              ) : (
                                <JudgeIcon />
                              )}
                            </Box>
                            <Box sx={WinnerTableStyle.bodyCellImageContent}>
                              <Typography variant={SUBTITLE1} sx={WinnerTableStyle.bodyCellImageContentName}>
                                {row.name}
                              </Typography>
                              <Typography variant={BODY1} sx={WinnerTableStyle.bodyCellImageContentCategory}>
                                {categories?.find((category) => category.id === row.categoryId)?.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>{currencyFormatter.format(row.winnerDetails?.winBidAmount || 0)}</TableCell>

                        <TableCell>
                          <ActionsMenu
                            disableRipple
                            content={
                              <Box>
                                {tableActionEntryMenuContent.map((item: MENU_OPTION) => {
                                  return (
                                    <Typography
                                      key={item}
                                      onClick={() => handleClickMenuOption(item, row)}
                                      variant={BODY1}
                                      sx={{
                                        ...auctionTableStyle.menuItem,
                                        ...(item === MENU_OPTION.CANCEL_WIN &&
                                          row.paymentStatus === PAYMENT_STATUS.PAID &&
                                          auctionTableStyle.disabledMenuItem),
                                        ...(item === MENU_OPTION.MARK_AS_PAID &&
                                          row.paymentStatus === PAYMENT_STATUS.PAID &&
                                          auctionTableStyle.disabledMenuItem),
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={WinnerTableStyle.pagination}>
                <Pagination
                  page={page}
                  pageSize={defaultPageSize}
                  total={auctionWonList?.totalCount || 0}
                  onChange={(step) => setPage(step)}
                />
              </Box>
            </Box>
          ) : (
            <EmptyTableData />
          )
        ) : (
          <Loader parentClassName={auctionStyle.loader} size={50} />
        )}
      </Box>
      <Drawer variant="permanent" anchor="bottom" sx={WinnerTableStyle.drawer}>
        <Box sx={WinnerTableStyle.buttonWrapper}>
          <Button title={ADD_WINNER} onClick={navigateToAddWinner} startIcon={<PlusIcon />} />
        </Box>
      </Drawer>

      <Modal
        open={isAddWinnerModalOpen}
        onClose={handleWinnerModal}
        onSave={handleAddWinner}
        sx={WinnerTableStyle.modal}
        mainButtonTitle={SAVE_CHANGES}
        closeButtonTitle={CANCEL}
        isShowSaveButton
        isShowCloseButton
        title={ADD_WINNER}
        isButtonsBottom
      >
        <AddWinnerPage formRef={formikRef} isModal={true} closeModal={handleWinnerModal} />
      </Modal>
      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
        />
      </Modal>

      <Modal
        onClose={handleCloseNotifyModal}
        open={isNoifyModalOpen}
        title="Action needed"
        description={notifyMessage}
        sx={WinnerTableStyle.modal}
      />
      <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={WinnerTableStyle.modal}
      />
    </Box>
  );
};

export default WinnerTable;
